import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Wind, Snowflake, Sun, DollarSign } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "How much can I save with proper insulation?",
    answer: "Homeowners can save 15-30% on heating and cooling costs with proper insulation. In Ontario's climate, payback periods are typically 2-5 years through energy savings and increased comfort."
  },
  {
    question: "What R-value do I need in Ontario?",
    answer: "Ontario Building Code requires minimum R-50 for attics, R-20 for exterior walls, and R-20-28 for basements. We assess your specific needs and recommend optimal R-values for maximum efficiency."
  },
  {
    question: "Is spray foam better than fiberglass?",
    answer: "Both have advantages. Spray foam provides superior air sealing and higher R-value per inch, ideal for hard-to-reach areas. Fiberglass is cost-effective for standard applications. We help you choose based on your needs and budget."
  },
  {
    question: "Can you add insulation to existing walls?",
    answer: "Yes, we offer blown-in insulation for existing walls without major renovation. We drill small access holes, fill cavities, and patch seamlessly for improved energy efficiency."
  }
];

const insulationServices = [
  {
    title: "Spray Foam Insulation",
    description: "High-performance closed-cell and open-cell spray foam for maximum R-value and air sealing.",
    icon: Wind,
    features: ["Air barrier creation", "Moisture control", "High R-value", "Long-term performance"]
  },
  {
    title: "Blown-In Insulation",
    description: "Cellulose and fiberglass blown-in for attics and existing wall cavities.",
    icon: Snowflake,
    features: ["Retrofit applications", "Complete coverage", "Eco-friendly options", "Cost-effective"]
  },
  {
    title: "Batt & Roll Insulation",
    description: "Traditional fiberglass and mineral wool batts for new construction and renovations.",
    icon: Sun,
    features: ["Easy installation", "Proven performance", "Sound dampening", "Fire-resistant options"]
  },
  {
    title: "Energy Audits",
    description: "Thermal imaging and blower door tests to identify air leaks and insulation gaps.",
    icon: DollarSign,
    features: ["Professional assessment", "Rebate eligibility", "Priority areas", "ROI analysis"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Kitchener-Waterloo", link: "/locations/kitchener-waterloo" }
];

export default function InsulationServices() {
  return (
    <>
      <SEOMeta
        title="Insulation Contractor — Energy Efficiency | Niagara • Hamilton • GTA"
        description="Professional insulation installation for homes and businesses in Niagara, Hamilton & the GTA. Warmer winters, cooler summers, lower bills."
        keywords="insulation contractor Niagara, Hamilton insulation services, GTA energy efficiency, spray foam, attic insulation, blown-in insulation, thermal barrier"
        canonicalUrl="https://blueknightcontracting.ca/services/insulation"
      />
      <LocalBusinessSchema
        locationName="Insulation Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional insulation services for energy efficiency and comfort"
        services={["Spray Foam Insulation", "Blown-In Insulation", "Batt Insulation", "Energy Audits"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Insulation Services
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Reduce energy costs and improve comfort across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Insulation Solutions</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From spray foam to blown-in cellulose, we provide the right insulation for every application
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {insulationServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Upgrade Your Insulation?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Lower Energy Bills</CardTitle>
                  <CardDescription>
                    Proper insulation reduces heating and cooling costs by up to 30%, paying for itself within years
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Improved Comfort</CardTitle>
                  <CardDescription>
                    Eliminate cold spots, drafts, and temperature fluctuations for consistent comfort year-round
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Rebate Eligible</CardTitle>
                  <CardDescription>
                    Many insulation upgrades qualify for provincial and federal energy efficiency rebates
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Start Saving on Energy Costs</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us for a free energy assessment and insulation quote
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Get Free Assessment</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
